import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import { GhostBadge } from '../ui/GhostBadge'
import { Button } from '../Common'
import styles from './Raid.module.css'

export type RaidStatusVariant = 'CURSE' | 'VICTORY'

interface CurseVariantProps {
  variant: 'CURSE'
}

interface VictoryVariantProps {
  variant: 'VICTORY'
  isAdmin: boolean
  busy?: boolean
  onDeclareVictory?: () => void
  onEndAsFailure?: () => void
}

type RaidStatusBannerProps = CurseVariantProps | VictoryVariantProps

/**
 * Curse variant → non-interactive GhostBadge pill in the containing pane's
 * top-right corner via `curseBadgeSlot`.
 *
 * Victory variant → pane overlay when currentBossHp === 0. Admin sees the
 * Declare Victory / End as Failure buttons directly in the card so they can
 * resolve without fighting the overlay z-index. Non-admins see a wait notice.
 * Does not auto-resolve — the raid stays in this state until Admin acts.
 */
export const RaidStatusBanner = (props: RaidStatusBannerProps) => {
  if (props.variant === 'CURSE') {
    return (
      <div className={styles.curseBadgeSlot}>
        <GhostBadge tone="critical" icon={<BoltRoundedIcon fontSize="inherit" />}>
          Curse Active
        </GhostBadge>
      </div>
    )
  }

  const { isAdmin, busy, onDeclareVictory, onEndAsFailure } = props

  return (
    <div className={styles.victoryOverlay} role="status" aria-live="polite">
      <div className={styles.victoryCard}>
        <span className={styles.victoryEyebrow}>
          <EmojiEventsRoundedIcon fontSize="inherit" style={{ verticalAlign: 'middle' }} /> Target Achieved
        </span>
        <h3 className={styles.victoryHeadline}>Boss Down</h3>
        {isAdmin ? (
          <>
            <p className={styles.victoryHint}>Choose how this raid closes out.</p>
            <div className={styles.victoryActions}>
              <Button
                variant="primary"
                icon={<EmojiEventsRoundedIcon />}
                loading={busy}
                onClick={onDeclareVictory}
              >
                Declare Victory
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={onEndAsFailure}
              >
                End as Failure
              </Button>
            </div>
          </>
        ) : (
          <p className={styles.victoryHint}>Waiting for an Admin to declare the outcome.</p>
        )}
      </div>
    </div>
  )
}
